import { z } from "zod";
import { OpportunityBoardFiltersSchema } from "@forgepoint/shared";
import {
  ARCHIVE_MODE_OPTIONS,
  CREATED_RANGE_PRESETS,
} from "./opportunityFilterTypes";

const STORAGE_KEY_PREFIX = "forgepoint.opportunityBoardFilters.";
const storageKey = (userId: string) => `${STORAGE_KEY_PREFIX}${userId}`;

const StoredBoardFiltersSchema = z.object({
  archiveMode: z.enum(ARCHIVE_MODE_OPTIONS.map((option) => option.value)),
  boardFilters: OpportunityBoardFiltersSchema.pick({
    salesRepUserIds: true,
    estimatorUserIds: true,
    taskAssigneeUserIds: true,
    companyIds: true,
    locationIds: true,
    customFields: true,
  }),
  createdRange: z
    .discriminatedUnion("kind", [
      z.object({
        kind: z.literal("preset"),
        preset: z.enum(CREATED_RANGE_PRESETS.map((preset) => preset.value)),
      }),
      z.object({
        kind: z.literal("custom"),
        from: z.coerce.date(),
        to: z.coerce.date().optional(),
      }),
    ])
    .nullable(),
});

/** The board's filter-bar selections as they are persisted between visits. The search text is deliberately left out. */
export type StoredBoardFilters = z.infer<typeof StoredBoardFiltersSchema>;

const DEFAULT_BOARD_FILTERS: StoredBoardFilters = {
  archiveMode: "active",
  boardFilters: {},
  createdRange: null,
};

/**
 * Reads the persisted selections, tolerating unavailable or corrupt storage.
 * @param userId - The signed-in user's id, if there is one
 * @returns The stored selections, or the unfiltered defaults when nothing usable is stored
 */
export function readBoardFilters(
  userId: string | undefined,
): StoredBoardFilters {
  if (!userId) {
    return DEFAULT_BOARD_FILTERS;
  }
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) {
      return DEFAULT_BOARD_FILTERS;
    }
    return StoredBoardFiltersSchema.parse(JSON.parse(raw));
  } catch {
    return DEFAULT_BOARD_FILTERS;
  }
}

/**
 * Persists the selections, ignoring storage failures.
 * @param userId - The signed-in user's id, if there is one
 * @param filters - The selections to persist
 * @returns Nothing
 */
export function writeBoardFilters(
  userId: string | undefined,
  filters: StoredBoardFilters,
): void {
  if (!userId) {
    return;
  }
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(filters));
  } catch {
    return;
  }
}
